// components/PostCreator/PostCreatorModal.tsx
import React from "react";
import { Modal } from "../../components/ui/Modal";
import { usePostCreator } from "./hooks/usePostCreator";
import { PostCreatorImageSelector } from "./PostCreatorImageSelector";
import { Post } from "@/core/types/postTypes";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { ENTITY_CONFIG } from "@/core/entityConfig";

interface PostCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string | null;
  editingPost?: Post | null;
  preSelectedMediaItems?: MediaItem[];
  /** When launched from an idea, scope the media picker to its media. */
  linkToIdeaId?: string | null;
  onSuccess?: (postId: string, response: any) => void;
  onError?: (error: string) => void;
}

// components/PostCreator/PostCreatorModal.tsx
export const PostCreatorModal: React.FC<PostCreatorModalProps> = ({
  isOpen,
  onClose,
  clientId,
  editingPost = null,
  preSelectedMediaItems,
  linkToIdeaId = null,
  onSuccess,
  onError,
}) => {
  const isEditMode = Boolean(editingPost);

  const postCreator = usePostCreator(clientId, {
    editingPost,
    initialMediaItems: preSelectedMediaItems,
    onSuccess: (postId, response) => {
      console.log(`Post ${isEditMode ? "updated" : "created"} successfully:`, {
        postId,
        response,
      });
      onSuccess?.(postId, response);
      handleClose();
    },
    onError: (error) => {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} post:`,
        error,
      );
      onError?.(error);
    },
  });

  const handleClose = () => {
    onClose();
    postCreator.resetForm();
  };

  const handleSave = async () => {
    const success = await postCreator.savePost();
    if (success) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? `Edit Post: ${editingPost?.id}` : ENTITY_CONFIG.post.createLabel}
      size="xl"
      className="max-w-7xl"
    >
      <div className="flex flex-col h-[85vh]">
        {/* Header */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditMode ? "Edit Post Images" : ENTITY_CONFIG.post.createLabel}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isEditMode
                  ? `Update images for post: ${editingPost?.id}`
                  : "Select images and arrange them"}
                {postCreator.isCarousel ? " in your preferred order" : ""}
              </p>
            </div>

            {/* Carousel toggle */}
            <div className="flex items-center gap-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={postCreator.isCarousel}
                  onChange={(e) => {
                    const isCarousel = e.target.checked;
                    postCreator.setIsCarousel(isCarousel);

                    // When switching to single mode, keep only first image
                    if (
                      !isCarousel &&
                      postCreator.selectedMediaItems.length > 1
                    ) {
                      postCreator.setSelectedMediaItems([
                        postCreator.selectedMediaItems[0],
                      ]);
                    }
                  }}
                  className="rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">Create carousel</span>
              </label>

              {postCreator.isCarousel && (
                <span className="text-sm text-gray-500">
                  Select up to 10 images (
                  {postCreator.selectedMediaItems.length}
                  /10)
                </span>
              )}

              {!postCreator.isCarousel &&
                postCreator.selectedMediaItems.length > 0 && (
                  <span className="text-sm text-gray-500">
                    Single image mode
                  </span>
                )}
            </div>
          </div>

          {/* Edit mode indicator */}
          {isEditMode && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                <span className="text-blue-700 text-sm font-medium">
                  Edit Mode: Update images for this post
                </span>
              </div>
            </div>
          )}

          {/* Error display */}
          {postCreator.error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <svg
                  className="w-4 h-4 text-red-500 mt-0.5 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="text-red-700 text-sm font-medium">
                  {postCreator.error}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ✅ Only show image selector - no form for edit mode */}
        <div className="flex-1 overflow-hidden">
          <PostCreatorImageSelector
            postCreator={postCreator}
            linkToIdeaId={linkToIdeaId}
          />
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-between">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
          >
            Cancel
          </button>

          <div className="flex space-x-2">
            {postCreator.selectedMediaItems.length > 0 && (
              <button
                onClick={() => postCreator.setSelectedMediaItems([])}
                disabled={postCreator.isCreating}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium disabled:opacity-50"
              >
                Clear Selection
              </button>
            )}

            <button
              onClick={handleSave}
              disabled={!postCreator.isValid || postCreator.isCreating}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {postCreator.isCreating ? (
                <>
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {isEditMode ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  {isEditMode ? "Update Images" : ENTITY_CONFIG.post.createLabel}
                  {postCreator.imageCount > 0 && ` (${postCreator.imageCount})`}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
